import {useEffect} from 'react';
import {v4 as uuid} from 'uuid';
import type {
  CaptionsDraft,
  CaptionsProjectResponse,
  RephrasingDraft,
  RephrasingProjectResponse,
  UpdateDraftProjectParams,
} from '@contentlab/api/project/types';
import {
  CreateDraftProjectParams,
  ProjectType,
} from '@contentlab/api/project/types';
import {
  Method,
  validateAiAvatarProject,
  validateCaptionsProject,
  validateDubbingProject,
  validateReformatProject,
  validateRephrasingProject,
  validateUpscaleProject,
} from '@contentlab/api/project/utils';
import useCreateDraftProject from '@contentlab/hooks/mutations/use-create-draft-project/useCreateDraftProject';
import useUpdateDraftProject from '@contentlab/hooks/mutations/use-update-draft-project/useUpdateDraftProject';
import contentLabStore, {
  contentLabActions,
  DraftAiAvatarProject,
  DraftCaptionsProject,
  DraftDubbingProject,
  DraftReformatProject,
  DraftRephrasingProject,
  DraftUpscaleProject,
  ErrorKind,
} from '@contentlab/stores/contentLabStore';
import {FeatureName, ProjectStatus} from '@contentlab/types/project';
import {handleContentLabError} from '@contentlab/utils/contentLabErrorHandling';
import getDefaultProjectName from '@contentlab/utils/getDefaultProjectName';
import {downloadAndParseSrt} from '@contentlab/utils/srtUtils';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {SESSION_ID_NOT_FOUND} from '@/lib/delta/deltaConstants.js';
import useProjectQuery from '@/hooks/content-studio/use-project-query';
import {
  extractAssetIdsFromValidatedDraft,
  fetchAndConvertAssetsToUploadedVideos,
  getMappedProjectByType,
} from './projectMappers';

const VALIDATOR_BY_TYPE = {
  [ProjectType.DUBBING]: validateDubbingProject,
  [ProjectType.REFORMAT]: validateReformatProject,
  [ProjectType.REPHRASING]: validateRephrasingProject,
  [ProjectType.CAPTIONS]: validateCaptionsProject,
  [ProjectType.AI_AVATAR]: validateAiAvatarProject,
  [ProjectType.UPSCALE]: validateUpscaleProject,
};

const DEFAULT_NAME_BY_TYPE = {
  [ProjectType.DUBBING]: 'Dub',
  [ProjectType.REFORMAT]: 'Resize',
  [ProjectType.REPHRASING]: 'Rescript',
  [ProjectType.CAPTIONS]: 'Captions',
  [ProjectType.AI_AVATAR]: 'Avatar',
  [ProjectType.UPSCALE]: 'Upscale',
};

// Mapping between FeatureName and ProjectType
const FEATURE_TO_PROJECT_TYPE: Partial<Record<FeatureName, ProjectType>> = {
  [FeatureName.AI_DUBBING]: ProjectType.DUBBING,
  [FeatureName.REFORMATTING]: ProjectType.REFORMAT,
  [FeatureName.REPHRASING]: ProjectType.REPHRASING,
  [FeatureName.CAPTIONS]: ProjectType.CAPTIONS,
  [FeatureName.AI_AVATAR]: ProjectType.AI_AVATAR,
  [FeatureName.UPSCALE]: ProjectType.UPSCALE,
};

interface UseManageDraftProjectParams {
  type: ProjectType;
  draftProject:
    | DraftDubbingProject
    | DraftReformatProject
    | DraftRephrasingProject
    | DraftCaptionsProject
    | DraftAiAvatarProject
    | DraftUpscaleProject;
  projectId?: string | null;
  onGetProjectErrorCallback?: (error?: Error) => void;
}

function useManageDraftProject({
  type,
  draftProject,
  projectId,
  onGetProjectErrorCallback,
}: UseManageDraftProjectParams) {
  const {data: project} = useProjectQuery({
    id: projectId,
    isDraft: true,
    onError: onGetProjectErrorCallback,
  });

  useEffect(() => {
    const loadProjectData = async () => {
      if (!project) {
        return;
      }

      if (!project.validatedDraft) {
        throw new Error('project does not have validatedDraft');
      }
      if (project.status !== ProjectStatus.DRAFT) {
        throw new Error('project is not draft');
      }
      if (FEATURE_TO_PROJECT_TYPE[project.feature] !== type) {
        throw new Error('project feature does not match');
      }

      const mappedProjectData = await getMappedProjectByType(project, type);

      // Extract asset IDs and fetch assets
      const assetIds = extractAssetIdsFromValidatedDraft(
        project.validatedDraft,
        project.feature
      );
      const uploadedVideos = await fetchAndConvertAssetsToUploadedVideos(
        assetIds,
        project.feature
      );

      // Update uploadedVideos in the store
      if (uploadedVideos.length > 0) {
        contentLabActions.setUploadedVideos(uploadedVideos);
      }

      // Load the project data into the appropriate draft project in contentLabStore
      switch (type) {
        case ProjectType.DUBBING: {
          contentLabActions.setDraftDubbingProject({
            ...(mappedProjectData as DraftDubbingProject),
          });
          break;
        }
        case ProjectType.REFORMAT: {
          contentLabActions.setDraftReformatProject({
            ...(mappedProjectData as DraftReformatProject),
          });
          break;
        }
        case ProjectType.UPSCALE: {
          contentLabActions.setDraftUpscaleProject({
            ...(mappedProjectData as DraftUpscaleProject),
          });
          break;
        }
        case ProjectType.REPHRASING: {
          contentLabActions.setDraftRephrasingProject({
            ...(mappedProjectData as DraftRephrasingProject),
          });
          break;
        }
        case ProjectType.CAPTIONS: {
          contentLabActions.setDraftCaptionsProject({
            ...(mappedProjectData as DraftCaptionsProject),
          });
          break;
        }
        case ProjectType.AI_AVATAR: {
          contentLabActions.setDraftAiAvatarProject({
            ...(mappedProjectData as DraftAiAvatarProject),
          });
          break;
        }
        default:
          break;
      }
    };

    try {
      loadProjectData();
    } catch (error) {
      console.error(error);
      onGetProjectErrorCallback?.();
    }
  }, [project, type]);
  // Create draft project mutation
  const createDraftProjectMutation = useCreateDraftProject({
    onSuccess: async (data, requestParams) => {
      if (data.type === ProjectType.DUBBING) {
        const currentState = contentLabStore.getState().draftDubbingProject;
        contentLabActions.setDraftDubbingProject({
          ...currentState,
          ...data,
          isInitialized: true,
        });
      } else if (data.type === ProjectType.REFORMAT) {
        const currentState = contentLabStore.getState().draftReformatProject;
        contentLabActions.setDraftReformatProject({
          ...currentState,
          ...data,
          isInitialized: true,
        });
      } else if (data.type === ProjectType.UPSCALE) {
        const currentState = contentLabStore.getState().draftUpscaleProject;
        contentLabActions.setDraftUpscaleProject({
          ...currentState,
          ...data,
          isInitialized: true,
        });
      } else if (data.type === ProjectType.REPHRASING) {
        const {rephrasingSpec} = requestParams.draft as RephrasingDraft;
        const rephrasingProjectData = data as RephrasingProjectResponse;
        const currentState = contentLabStore.getState().draftRephrasingProject;

        if (rephrasingProjectData.transcriptionUrl) {
          const mappedSrt = await downloadAndParseSrt(
            rephrasingProjectData.transcriptionUrl
          );

          contentLabActions.setDraftRephrasingProject({
            ...currentState,
            ...data,
            isInitialized: true,
            transcriptionLoaded: true,
            currentSrt: mappedSrt,
            assetId: rephrasingSpec.assetId,
          });
        } else {
          contentLabActions.setDraftRephrasingProject({
            ...currentState,
            ...data,
            isInitialized: true,
            assetId: rephrasingSpec.assetId,
          });
        }
      } else if (data.type === ProjectType.CAPTIONS) {
        const captionsDraft = requestParams.draft as CaptionsDraft;
        const captionsProjectData = data as CaptionsProjectResponse;
        const currentState = contentLabStore.getState().draftCaptionsProject;

        if (captionsProjectData.transcriptionUrl) {
          const mappedSrt = await downloadAndParseSrt(
            captionsProjectData.transcriptionUrl
          );

          contentLabActions.setDraftCaptionsProject({
            ...currentState,
            ...data,
            isInitialized: true,
            transcriptionLoaded: true,
            currentSrt: mappedSrt,
            originalSrt: mappedSrt,
            assetId: captionsDraft.assetId,
            originalLanguage: captionsProjectData.language?.name || '',
            apiOriginalLanguage: captionsProjectData.language || null,
            selectedLanguages: captionsProjectData.language?.name
              ? [captionsProjectData.language.name]
              : [],
          });
        } else {
          contentLabActions.setDraftCaptionsProject({
            ...currentState,
            ...data,
            isInitialized: true,
            transcriptionLoaded: false, // Keep false until transcription is ready
            assetId: captionsDraft.assetId,
            originalLanguage: captionsProjectData.language?.name || '',
            apiOriginalLanguage: captionsProjectData.language || null,
            selectedLanguages: captionsProjectData.language?.name
              ? [captionsProjectData.language.name]
              : [],
          });
        }
      } else if (data.type === ProjectType.AI_AVATAR) {
        const currentState = contentLabStore.getState().draftAiAvatarProject;
        contentLabActions.setDraftAiAvatarProject({
          ...currentState,
          ...data,
          isInitialized: true,
        });
      }
    },
    onError: (error) => {
      handleContentLabError(
        error,
        ErrorKind.FAILURE,
        'Failed to initialize draft project'
      );
    },
  });

  // Update draft project mutation
  const updateDraftProjectMutation = useUpdateDraftProject({
    onSuccess: async (data, requestParams) => {
      if (data.type === ProjectType.DUBBING && draftProject?.isInitialized) {
        // Preserve the projectId since the update response doesn't include it
        contentLabActions.setDraftDubbingProject({
          ...draftProject,
          ...(data as DraftDubbingProject),
          isInitialized: true,
        });
      } else if (
        data.type === ProjectType.REFORMAT &&
        draftProject?.isInitialized
      ) {
        contentLabActions.setDraftReformatProject({
          ...draftProject,
          ...(data as DraftReformatProject),
          isInitialized: true,
          projectId: draftProject?.projectId,
        });
      } else if (
        data.type === ProjectType.REPHRASING &&
        draftProject?.isInitialized
      ) {
        const rephrasingProject = data as RephrasingProjectResponse;
        const {rephrasingSpec} = requestParams.draft as RephrasingDraft;

        // If the assetId has changed, update with new transcription
        if (
          (draftProject as DraftRephrasingProject).assetId !==
            rephrasingSpec.assetId &&
          rephrasingProject.transcriptionUrl
        ) {
          const mappedSrt = await downloadAndParseSrt(
            rephrasingProject.transcriptionUrl
          );

          contentLabActions.setDraftRephrasingProject({
            ...draftProject,
            ...(data as DraftRephrasingProject),
            isInitialized: true,
            transcriptionLoaded: true,
            currentSrt: mappedSrt,
            assetId: rephrasingSpec.assetId,
          });
        } else {
          contentLabActions.setDraftRephrasingProject({
            ...draftProject,
            ...(data as DraftRephrasingProject),
            isInitialized: true,
          });
        }
      } else if (
        data.type === ProjectType.CAPTIONS &&
        draftProject?.isInitialized
      ) {
        const captionsProject = data as CaptionsProjectResponse;
        const captionsDraft = requestParams.draft as CaptionsDraft;

        // If the assetId has changed, update with new transcription
        if (
          (draftProject as DraftCaptionsProject).assetId !==
            captionsDraft.assetId &&
          captionsProject.transcriptionUrl
        ) {
          const mappedSrt = await downloadAndParseSrt(
            captionsProject.transcriptionUrl
          );

          contentLabActions.setDraftCaptionsProject({
            ...draftProject,
            ...(data as DraftCaptionsProject),
            isInitialized: true,
            transcriptionLoaded: true,
            currentSrt: mappedSrt,
            originalSrt: mappedSrt,
            assetId: captionsDraft.assetId,
            originalLanguage: captionsProject.language?.name || '',
            apiOriginalLanguage: captionsProject.language || null,
            selectedLanguages: captionsProject.language?.name
              ? [captionsProject.language.name]
              : [],
          });
        } else {
          const currentSelectedLanguages = (
            draftProject as DraftCaptionsProject
          ).selectedLanguages;
          contentLabActions.setDraftCaptionsProject({
            ...draftProject,
            ...(data as DraftCaptionsProject),
            isInitialized: true,
            originalLanguage: captionsProject.language?.name || '',
            apiOriginalLanguage: captionsProject.language || null,
            selectedLanguages:
              currentSelectedLanguages.length === 0 &&
              captionsProject.language?.name
                ? [captionsProject.language.name]
                : currentSelectedLanguages,
          });
        }
      } else if (
        data.type === ProjectType.AI_AVATAR &&
        draftProject?.isInitialized
      ) {
        contentLabActions.setDraftAiAvatarProject({
          ...draftProject,
          ...(data as DraftAiAvatarProject),
        });
      }
    },
    onError: (error) => {
      handleContentLabError(
        error,
        ErrorKind.FAILURE,
        'Failed to update draft project'
      );
    },
  });

  const createDraftProject = async (params: CreateDraftProjectParams) => {
    const validator = VALIDATOR_BY_TYPE[type];
    if (!validator(Method.CREATE, params as CreateDraftProjectParams)) {
      return undefined;
    }

    const defaultProjectName = getDefaultProjectName(
      DEFAULT_NAME_BY_TYPE[type]
    );

    return createDraftProjectMutation.mutateAsync({
      ...params,
      projectName: params.projectName || defaultProjectName,
      draft: {
        ...params.draft,
      },
    });
  };

  const updateDraftProject = async (
    params: Omit<UpdateDraftProjectParams, 'type' | 'analyticsParams'>
  ) => {
    const validator = VALIDATOR_BY_TYPE[type];
    if (!validator(Method.UPDATE, params as UpdateDraftProjectParams)) {
      return undefined;
    }

    if (!draftProject?.isInitialized) {
      throw new Error('No draft project available for update');
    }

    const interactionId = uuid();
    const sessionId = AnalyticsService.getSessionId() || SESSION_ID_NOT_FOUND;

    const defaultProjectName = getDefaultProjectName(
      DEFAULT_NAME_BY_TYPE[type]
    );

    const updateParams: UpdateDraftProjectParams = {
      ...params,
      type,
      projectName: params.projectName || defaultProjectName,
      analyticsParams: {
        interactionId,
        sessionId,
      },
    };

    return updateDraftProjectMutation.mutateAsync({
      projectId: draftProject.projectId!,
      params: updateParams,
    });
  };

  return {
    data: updateDraftProjectMutation.data || createDraftProjectMutation.data,
    createDraftProject,
    updateDraftProject,
    resetDraftProject: () => {
      switch (type) {
        case ProjectType.DUBBING:
          contentLabActions.removeFeatureUploadingFiles(FeatureName.AI_DUBBING);
          contentLabActions.removeFeatureUploadedVideos(FeatureName.AI_DUBBING);
          contentLabActions.removeFeatureAssetErrors(FeatureName.AI_DUBBING);
          contentLabActions.resetDraftDubbingProject();
          break;
        case ProjectType.REFORMAT:
          contentLabActions.removeFeatureUploadingFiles(
            FeatureName.REFORMATTING
          );
          contentLabActions.removeFeatureUploadedVideos(
            FeatureName.REFORMATTING
          );
          contentLabActions.removeFeatureAssetErrors(FeatureName.REFORMATTING);
          contentLabActions.resetDraftReformatProject();
          break;
        case ProjectType.REPHRASING:
          contentLabActions.removeFeatureUploadingFiles(FeatureName.REPHRASING);
          contentLabActions.removeFeatureUploadedVideos(FeatureName.REPHRASING);
          contentLabActions.removeFeatureAssetErrors(FeatureName.REPHRASING);
          contentLabActions.resetDraftRephrasingProject();
          break;
        case ProjectType.CAPTIONS:
          contentLabActions.removeFeatureUploadingFiles(FeatureName.CAPTIONS);
          contentLabActions.removeFeatureUploadedVideos(FeatureName.CAPTIONS);
          contentLabActions.removeFeatureAssetErrors(FeatureName.CAPTIONS);
          contentLabActions.resetDraftCaptionsProject();
          break;
        default:
          break;
      }
    },
    isLoading:
      createDraftProjectMutation.isLoading ||
      updateDraftProjectMutation.isLoading,
  };
}

export default useManageDraftProject;
